import { Observable, Subject } from 'rxjs';

const collapsedAccounts: Set<string> = new Set();
let collapsedFolders: {
  [accountId: string]: string[];
} = {};
let visibleEmptyFolders: {
  [accountId: string]: string[];
} = {};
let favouritesCollapsed = false;

const _expandedFoldersChange$ = new Subject<void>();
const expandedFoldersChange$ = _expandedFoldersChange$.asObservable();

export function getExpandedFoldersChange$(): Observable<void> {
  return expandedFoldersChange$;
}

// GETTERS

export const GET_FAVOURITES_COLLAPSED = (): boolean => favouritesCollapsed;

export const GET_COLLAPSED_ACCOUNTS = (): Set<string> => collapsedAccounts;

export const GET_COLLAPSED_FOLDERS = (): {
  [accountId: string]: string[];
} => collapsedFolders;

export const GET_VISIBLE_EMPTY_FOLDERS = (): {
  [accountId: string]: string[];
} => visibleEmptyFolders;

// SETTERS

export const ADD_VISIBLE_EMPTY_FOLDER = (
  _accountId: string,
  _folderName: string,
  sendUpdate = true,
): void => {
  if (!visibleEmptyFolders[_accountId]) {
    visibleEmptyFolders[_accountId] = [_folderName];
  } else if (!visibleEmptyFolders[_accountId].includes(_folderName)) {
    visibleEmptyFolders[_accountId].push(_folderName);
  }

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const SET_FAVOURITES_COLLAPSED = (_collapsed: boolean, sendUpdate = true): void => {
  favouritesCollapsed = _collapsed;

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const SET_COLLAPSED_ACCOUNTS = (newCollapsedAccounts: string[], sendUpdate = true): void => {
  collapsedAccounts.clear();
  newCollapsedAccounts.forEach((account) => collapsedAccounts.add(account));

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const ADD_COLLAPSED_ACCOUNT = (_accountId: string, sendUpdate = true): void => {
  collapsedAccounts.add(_accountId);

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const ADD_COLLAPSED_FOLDER = (
  _accountId: string,
  _folderId: string,
  sendUpdate = true,
): void => {
  if (!collapsedFolders[_accountId]) {
    collapsedFolders[_accountId] = [_folderId];
  } else {
    if (!collapsedFolders[_accountId].includes(_folderId)) {
      collapsedFolders[_accountId].push(_folderId);
    }
  }

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const DELETE_COLLAPSED_ACCOUNT = (_accountId: string, sendUpdate = true): void => {
  collapsedAccounts.delete(_accountId);

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const DELETE_COLLAPSED_FOLDER = (
  _accountId: string,
  _folderId: string,
  sendUpdate = true,
): void => {
  if (!collapsedFolders[_accountId]) {
    return;
  }

  const index = collapsedFolders[_accountId].indexOf(_folderId);

  if (index === -1) {
    return;
  }

  collapsedFolders[_accountId].splice(index, 1);
  if (collapsedFolders[_accountId].length === 0) {
    collapsedFolders = Object.keys(collapsedFolders)
      .filter((accountId) => accountId !== _accountId)
      .reduce<{ [accountId: string]: string[] }>((acc, accountId) => {
        acc[accountId] = collapsedFolders[accountId];
        return acc;
      }, {});
  }
  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const SET_COLLAPSED_FOLDERS = (
  newCollapsedFolders: { [accountId: string]: string[] },
  sendUpdate = true,
): void => {
  collapsedFolders = newCollapsedFolders;

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const CLEAR_COLLAPSED_FOLDERS = (sendUpdate = true): void => {
  collapsedFolders = {};

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const CLEAR_COLLAPSED_ACCOUNTS = (sendUpdate = true): void => {
  collapsedAccounts.clear();

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const CLEAR_FAVOURITES_COLLAPSED = (sendUpdate = true): void => {
  favouritesCollapsed = false;

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const CLEAR_VISIBLE_EMPTY_FOLDERS = (sendUpdate = true): void => {
  visibleEmptyFolders = {};

  SEND_ACCOUNT_LIST_UPDATE_EVENT(sendUpdate);
};

export const SEND_ACCOUNT_LIST_UPDATE_EVENT = (sendUpdate = true): void => {
  if (sendUpdate) {
    _expandedFoldersChange$.next();
  }
};
